import React from "react";
import { FormattedMessage } from "react-intl";

import { cn } from "@/utils/cn";

import Stack from "../stack/stack";
import Text from "../text/text";

interface SpinnerProps {
  /** Width and height of the spinner in pixels. */
  size?: number;
  /** Whether to display "Loading..." beneath the spinner. */
  withText?: boolean;
  /** Whether to add space around the spinner */
  withSpace?: boolean;
  /** Color of spinner */
  theme?: "default" | "white";
}

/** Spinning loading placeholder. */
const Spinner = ({
  size = 30,
  withText = true,
  theme = "default",
  withSpace = false,
}: SpinnerProps) => (
  <Stack
    space={2}
    justifyContent="center"
    alignItems="center"
    className={withSpace ? "py-4" : ""}
    data-testid="spinner"
    role="status"
  >
    <div
      className="relative inline-block"
      style={{ width: size, height: size }}
    >
      {Array.from(Array(12).keys()).map((i) => (
        <div
          key={i}
          className={cn({
            "absolute h-full w-full origin-[50%_50%] animate-spinner": true,
            "after:content-[_ _] after:absolute after:top-[3.75%] after:left-[46.25%] after:block after:h-[22.5%] after:w-[7.5%] after:rounded-full": true,
            "after:bg-gray-700 dark:after:bg-gray-400": theme === "default",
            "after:bg-white dark:after:bg-white": theme === "white",
            "nth-1:rotate-0 nth-1:animate-delay-[-1.1s]": true,
            "nth-2:rotate-30 nth-2:animate-delay-[-1s]": true,
            "nth-3:rotate-60 nth-3:animate-delay-[-0.9s]": true,
            "nth-4:rotate-90 nth-4:animate-delay-[-0.8s]": true,
            "nth-5:rotate-120 nth-5:animate-delay-[-0.7s]": true,
            "nth-6:rotate-150 nth-6:animate-delay-[-0.6s]": true,
            "nth-7:rotate-180 nth-7:animate-delay-[-0.5s]": true,
            "nth-8:rotate-210 nth-8:animate-delay-[-0.4s]": true,
            "nth-9:rotate-240 nth-9:animate-delay-[-0.3s]": true,
            "nth-10:rotate-270 nth-10:animate-delay-[-0.2s]": true,
            "nth-11:rotate-300 nth-11:animate-delay-[-0.1s]": true,
            "nth-12:animate-delay-0 nth-12:rotate-330": true,
          })}
        >
          &nbsp;
        </div>
      ))}
    </div>

    {withText && (
      <Text theme="muted" tracking="wide">
        <FormattedMessage
          id="loading_indicator.label"
          defaultMessage="Loading…"
        />
      </Text>
    )}
  </Stack>
);

export default Spinner;
